<template>
    <mini-calculator-page>
        <div slot="summary-panel">
            <div class="p-animated-calc__results a-form-inputs u-full-width u-margin-auto p-interest__calc">
                <mini-calculator-header title="Interest Calculator" icon="interest"/>

                <control for="date_from" label="Date From">
                    <date-input v-model="dateFrom"
                                ref="dateFrom"
                                v-field-control:date_from.required="{type:'date'}"
                                @input="updateTotal()"></date-input>
                    <control-error :show="$error('date_from')">
                        The date from is required and must be in the format DD/MM/YYYY
                    </control-error>
                </control>
                <control for="date_to" label="Date To">
                    <date-input v-model="dateTo"
                                ref="dateTo"
                                v-field-control:date_to.required="{type:'date'}"
                                @input="updateTotal()"></date-input>
                    <control-error :show="$error('date_to') || !dateRules">
                        The date to is required and must be in the format DD/MM/YYYY
                    </control-error>
                </control>
                <control for="interest_rate" label="Interest Rate">
                    <input autocomplete="off" v-model.number="interestRate"
                           v-field-control
                           v-two-dp
                           ref="interestRate"
                           required
                           min="0"
                           name="interest_rate"
                           type="number"
                           @input="updateTotal()">
                    <control-error :show="$error('interest_rate')">
                        The Interest Rate must be greater than 0
                    </control-error>
                </control>
                <control for="principal_amount" label="Principal Amount" before-after="pound">
                    <input autocomplete="off" v-model.number="principalAmount"
                           v-field-control
                           v-on:focus="principalAmount = principalAmount || ''"
                           v-on:blur="principalAmount = principalAmount || 0"
                           v-two-dp
                           ref="principalAmount"
                           required
                           min="0"
                           name="value"
                           type="number"
                           @input="updateTotal()">
                    <control-error :show="$error('principal_amount')">
                        The Amount must be greater than &pound;0
                    </control-error>
                </control>
                <control for="interest_type" label="Interest Type">
                    <select v-field-control
                            required
                            name="interest_type"
                            v-model="interestType"
                            ref="interestType"
                            @change="updateTotal()">
                        <option :value="null">Please select...</option>
                        <option value="simple">Simple</option>
                        <option value="compound">Compound</option>
                    </select>
                    <control-error :show="$error('interest_type')">
                        Please select an Interest Type
                    </control-error>
                </control>
                <control for="compound_frequency" label="Compound Frequency" v-if="interestType === 'compound'">
                    <select v-field-control
                            required
                            name="compound_frequency"
                            v-model="compoundFrequency"
                            @change="updateTotal()"
                            ref="compoundFrequency">
                        <option value="52">Weekly</option>
                        <option value="12">Monthly</option>
                        <option value="4">Quarterly</option>
                        <option value="1">Yearly</option>
                    </select>
                    <control-error :show="$error('compound_frequency')">
                        Please select a Compound Frequency
                    </control-error>
                </control>

                <h2 class="u-r-margin-top-40">
                    Results
                </h2>
                <control for="interest_accrued" label="Interest Accrued" before-after="pound-on-dark">
                    <input autocomplete="off" v-model.number="interestAccrued"
                           v-two-dp
                           disabled
                           name="interest_accrued">
                </control>

                <control for="theTotal" label="Total" before-after="pound-on-dark">
                    <input autocomplete="off" v-model.number="theTotal"
                           v-two-dp
                           disabled
                           name="theTotal">
                </control>
            </div>
        </div>

        <div slot="results-panel" v-show="showResults">
            <div class="p-animated-calc__results-panel u-full-width u-margin-auto">
                <h2>
                    Explanation
                </h2>

                <p>
                    The accrued interest between the period of <strong>{{ dateFrom }}</strong> and <strong>{{ dateTo
                    }}</strong>
                    on the principal of <strong>{{ currencyFormat(principalAmount) }}</strong>, with an interest rate of
                    <strong>{{ interestRate }}%</strong>,
                    using
                    <span v-if="interestType === 'compound'">the <strong>compound</strong> interest calculation with
                        compounding occurring <strong>{{ frequency }}</strong></span>
                    <span v-else>the <strong>simple</strong> interest calculation</span>
                    is calculated using the following formula:
                </p>

                <div class="m-formula">
                    <div class="m-formula__item--row">
                        <strong>
                            <span v-if="interestType === 'compound'">
                                [£ Total] = [£ Principal (1 + [Interest Rate %]/[Periods])[Periods] * [Duration]
                            </span>
                            <span v-else>
                                [£ Total] = [£ Principal] x [1 + Interest Rate %] x [Duration]
                            </span>
                        </strong>
                    </div>
                </div>

                <p v-if="interestType === 'compound'">
                    Where <strong>duration</strong> is the accrual length in years and <strong>periods</strong> is the
                    number of
                    compounding periods that occur each year (e.g., compounding quarterly results in 4 periods per
                    year).
                </p>

                <p>
                    This results in <strong>{{ currencyFormat(interestAccrued) }}</strong> of interest having been
                    accrued,
                    resulting in a total figure of <strong>{{ currencyFormat(theTotal) }}</strong>. This has been
                    displayed
                    on the following graph for you:
                </p>


                <div v-if="theTotal" class="chart chart--bar">
                    <line-chart :chart-data="chartData" :options="chartOptions"></line-chart>
                </div>

                <div class="p-interest__calc-results-group">
                    <div>Aggregate Rate: <span>{{ roundValue(aggregateRate / 100, 2) }}%</span></div>
                    <div>Number of Days: <span>{{ noOfDays }}</span></div>
                    <div>Interest per day: <span>{{ currencyFormat(dailyRate) }}</span></div>
                </div>
            </div>
        </div>
    </mini-calculator-page>
</template>

<script type="text/babel">
    import Vue from 'vue';
    import moment from 'moment';
    import {mapGetters, mapActions} from 'vuex';
    import MiniCalculatorPage from 'pages/mini-calculators/components/MiniCalculatorPage';
    import miniCalcIcons from 'components/Icons.vue';
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import FormValidator from 'components/form/validator/mixin';
    import DateInput from 'components/form/control/DateInput.vue';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';
    import CalculatorKeyBindings from 'pages/mini-calculators/calculators/mixins/MiniCalculatorBindings';
    import LineChart from 'components/graphs/LineChart.js';
    import roundValue from 'mixins/roundValue';
    import Intro from 'pages/mini-calculators/calculators/CalculatorComponents/Intro';
    import currencyFormat from 'mixins/currencyFormat';
    import MiniCalculatorHeader from 'pages/mini-calculators/components/MiniCalculatorHeader';

    export default {
        name: 'age-calculator',
        computed: {
            ...mapGetters('mini/interest', {
                getValue: 'value'
            }),
            dateFrom: {
                get() {
                    return this.getValue('dateFrom');
                },
                set(value) {
                    this.updateValue({key: 'dateFrom', value});
                }
            },
            dateTo: {
                get() {
                    return this.getValue('dateTo');
                },
                set(value) {
                    this.updateValue({key: 'dateTo', value});
                }
            },
            interestRate: {
                get() {
                    return this.getValue('interestRate');
                },
                set(value) {
                    this.updateValue({key: 'interestRate', value});
                }
            },
            principalAmount: {
                get() {
                    return this.getValue('principalAmount');
                },
                set(value) {
                    this.updateValue({key: 'principalAmount', value});
                }
            },
            interestType: {
                get() {
                    return this.getValue('interestType');
                },
                set(value) {
                    this.updateValue({key: 'interestType', value});
                }
            },
            compoundFrequency: {
                get() {
                    return this.getValue('compoundFrequency');
                },
                set(value) {
                    this.updateValue({key: 'compoundFrequency', value});
                }
            },
            interestAccrued: {
                get() {
                    return this.getValue('interestAccrued');
                }
            },
            aggregateRate: {
                get() {
                    return this.getValue('aggregateRate');
                },
            },
            noOfDays: {
                get() {
                    return this.getValue('noOfDays');
                },
            },
            dailyRate: {
                get() {
                    return this.getValue('dailyRate');
                },
            },
            theTotal: {
                get() {
                    return this.getValue('theTotal');
                },
            },
            dateRules() {
                return this.dateTo ? (this.dateTo > this.dateFrom) : true;
            },
            totalInputs() {
                return this.interestType === 'compound' ? 7 : 6;
            },
            frequency() {
                switch (this.compoundFrequency) {
                    case '52':
                        return 'weekly';
                    case '12':
                        return 'monthly';
                    case '4':
                        return 'quarterly';
                    case '1':
                        return 'yearly';
                }
            }
        },
        watch: {
            theTotal: function (value, newValue) {
                if (value !== newValue) {
                    this.hidePrompt();
                    this.showResults = true;
                    this.updateChart();
                }
            }
        },
        mixins: [
            roundValue,
            FormValidator,
            CalculatorKeyBindings,
            currencyFormat
        ],
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        components: {
            Control,
            ControlError,
            DateInput,
            Intro,
            MiniCalculatorPage,
            MiniCalculatorHeader,
            miniCalcIcons,
            LineChart,
        },
        data: function () {
            return {
                chartData: {},
                chartOptions: {},
                showResults: false
            }
        },
        created() {
            this.resetForm()
        },
        methods: {
            ...mapActions('mini/interest', {
                resetForm: 'reset',
                updateValue: 'update',
                calcInterest: 'calcInterest'
            }),
            updateTotal() {
                this.calcInterest().then(() => {
                    if (!this.$valid()) {
                        return;
                    }

                    this.hidePrompt();
                    this.showResults = true;
                });
            },
            resetTheForm() {
                this.resetForm();
            },
            updateChart() {
                let mDateTo = moment(this.dateTo, 'DD/MM/YYYY');
                let mDateFrom = moment(this.dateFrom, 'DD/MM/YYYY');
                let diff = mDateTo.diff(mDateFrom, 'days');

                this.chartData = {
                    labels: [this.dateFrom, mDateFrom.add(diff / 2, 'days').format('DD/MM/YYYY'), this.dateTo],
                    datasets: [
                        {
                            label: 'Principal Amount',
                            type: 'line',
                            backgroundColor: [
                                '#42E6F2',
                            ],
                            borderColor: [
                                '#42E6F2',
                            ],
                            fill: false,
                            data: [this.principalAmount, this.principalAmount, this.principalAmount],
                        },
                        {
                            label: 'Total including interest',
                            type: 'line',
                            backgroundColor: [
                                '#4A89E5'
                            ],
                            borderColor: [
                                '#4A89E5'
                            ],
                            fill: false,
                            data: [this.principalAmount, this.calcMiddlePoint(), this.theTotal],
                        }
                    ]
                };

                this.chartOptions = {
                    scales: {
                        yAxes: [{
                            ticks: {
                                userCallback: (value, index, values) => {
                                    return this.currencyFormat(value);
                                }
                            }
                        }]
                    }
                }
            },
            calcMiddlePoint() {
                if (this.interestType === 'simple') {
                    return (this.interestAccrued / 2) + this.principalAmount;
                }
            }
        },
    }
</script>


// WEBPACK FOOTER //
// Interest.vue?332a9b64